import { ButtonLink, Icon, WithNAFallback } from '@toggle/design-system';
import { getPriceStatus } from '@toggle/helpers';
import { EconomicEventOutput, SnakeMeta } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useChartNavigate } from '~/hooks/use-chart-navigate/useChartNavigate';
import { useSnakeMeta } from '~/react-query-hooks/snakes/useSnake';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';

import { RunScenario } from '../run-scenario/RunScenario';
import { SummaryTable } from '../summary-table/SummaryTable';
import { getConditionOptions } from '../utils/getConditionOptions';
import { formatNumber } from '../utils/number-formatter';
import * as S from './EconomicsEventDrawerContent.styles';

export interface EconomicsEventDrawerContentProps {
  data: EconomicEventOutput;
}

export const EconomicsEventDrawerContent = ({
  data,
}: EconomicsEventDrawerContentProps) => {
  const { t } = useTranslation(['calendar', 'chart']);
  const chartNavigate = useChartNavigate();
  const { trackChartOpen } = usePosthogEvents();

  const {
    category,
    period,
    period_year,
    description,
    consensus,
    consensus_unit,
    actual,
    actual_unit: actualUnit,
    surprise,
    surprise_unit,
    name,
    snakes,
  } = data;

  const snakeName = snakes?.actual;
  const { data: snakeData, isFetching: isFetchingSnakeData } =
    useSnakeMeta(snakeName);

  const isBooleanEvent = !!snakes?.event;
  const actualUnitHeader = actualUnit ? [`(${actualUnit})`] : [''];

  const tableHeaders = [
    ...actualUnitHeader,
    t('calendar:expected'),
    t('calendar:reported'),
    t('calendar:surprise'),
  ];

  const tableRows = [
    {
      fieldName: name,
      consensus: (
        <WithNAFallback
          value={
            consensus !== undefined &&
            consensus !== null &&
            formatNumber({
              isPercentage: consensus_unit === '%',
              value: consensus,
            })
          }
        />
      ),
      reported: (
        <WithNAFallback
          value={
            actual !== undefined &&
            actual !== null &&
            formatNumber({ isPercentage: actualUnit === '%', value: actual })
          }
        />
      ),
      surprise: (
        <S.SurpriseWrapper $status={getPriceStatus(surprise ?? undefined)}>
          <WithNAFallback
            value={
              surprise !== undefined &&
              surprise !== null &&
              formatNumber({
                isPercentage: (surprise_unit ?? actualUnit) === '%',
                value: surprise,
              })
            }
          />
        </S.SurpriseWrapper>
      ),
    },
  ];

  const formattedPeriodLabel = period
    ? `${period}, ${period_year}`
    : period_year;
  const dateTitleLabel = `${category} ${formattedPeriodLabel}`;

  const conditionOptions = getConditionOptions({ t, data });

  const onClick = (snake: SnakeMeta) =>
    chartNavigate([[snake.entity, snake.snake, 0]]);

  return (
    <S.Container>
      <S.DateTitleWrapper data-testid="title-label">
        {dateTitleLabel}
      </S.DateTitleWrapper>
      {description && (
        <S.DescriptionWrapper data-testid="description-label">
          {description}
        </S.DescriptionWrapper>
      )}
      <SummaryTable
        title={
          <S.StyledTitleWrapper>
            <S.TitleWrapper>{t('calendar:summary')}</S.TitleWrapper>
            {!!snakeData && !isFetchingSnakeData && (
              <ButtonLink
                data-testid="open-in-chart"
                label={
                  <>
                    {t('chart:openIn')}
                    <Icon iconName={'ChartMixed'} size={16} />
                    {t('chart:title')}
                  </>
                }
                onClick={() => {
                  trackChartOpen('events');
                  onClick(snakeData);
                }}
              />
            )}
          </S.StyledTitleWrapper>
        }
        tableHeaders={tableHeaders}
        tableRows={tableRows}
      />
      {!isBooleanEvent && !!conditionOptions.length && (
        <RunScenario data={data} conditionOptions={conditionOptions} />
      )}
    </S.Container>
  );
};
